import React from 'react';

import { FlexColumn, FlexRow } from '@editor/components/utilities/layouts';
import { withEventScope } from '@repo/event-processor';
import { SubscriptionPlanType } from '@repo/subscription/api';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';
import { Badge } from '@repo/ui/components/Badge';
import { IconButton } from '@repo/ui/components/Buttons';
import HelpIcon from '@repo/ui/components/Icon/HelpIcon';
import PremiumBadgeIcon from '@repo/ui/components/Icon/PremiumBadgeIcon';
import { Spacer } from '@repo/ui/components/Spacer';
import { H2, H5, P2 } from '@repo/ui/components/Typography';
import { themeStyle, useNewThemeActive } from '@repo/ui/theming';
import { HELP_PAGE_URL, PRICING_PAGE_URL } from '@repo/utils/url';

import useFreeTrial from '../../../../hooks/useFreeTrial';
import {
  usePlanSelectionStore,
  planSelectionStoreSelector,
} from '../../../../stores/planSelectionStore';
import PricingPlan from '../../../PricingPlan/PricingPlan';
import Tag from '../../../Tag/Tag';
import { specialTheme } from '../../../Tag/theme';
import { getFreeTrialConfig } from '../../../UpgradeFlowModal/config'; // TODO: reconsider this - probably, we need smth in a global store.
import { HideMobile } from '../../../utilities/queries';
import { Divider } from '../../../utilities/styles';
import usePricingPlansData from '../../hooks/usePricingPlansData';
import useSelectedAddon from '../../hooks/useSelectedAddon';

import { CompareLink, PlansContainer, SelectButton, Wrapper } from './styles';

const helpTheme = {
  color: themeStyle.varInkMedium,
  activeColor: themeStyle.varInkMain,
  fontSize: themeStyle.fontSize12,
  lineHeight: '15px',
  fontWeight: themeStyle.fontMedium,
};

/**
 * This component mounts the plan selector.
 */
const PlanSelector: React.FC<{
  disabled: boolean;
}> = ({ disabled }) => {
  const selectedAddon = useSelectedAddon();
  const pricingPlansData = usePricingPlansData({ addon: selectedAddon });

  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const upgradeToExpertPlan = usePlanSelectionStore(
    planSelectionStoreSelector.upgradeToExpertPlan
  );
  const { setPlan } = usePlanSelectionStore(planSelectionStoreSelector.actions);
  const upgrade = usePlanSelectionStore(planSelectionStoreSelector.upgrade);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);
  const quantity = usePlanSelectionStore(planSelectionStoreSelector.quantity);
  const freeTrialConfig = getFreeTrialConfig({ color: themeStyle.varInkMain });
  const tag = freeTrialConfig.tag;
  const freeTrial = useFreeTrial();

  const disabledSelector = disabled || (upgrade && !!currentSubscription?.plan);

  const isNewThemeActive = useNewThemeActive();
  let tagContent = null;

  if (tag) {
    tagContent = isNewThemeActive ? (
      <Badge text={tag} variant="blue" />
    ) : (
      <Tag theme={specialTheme}>{tag}</Tag>
    );
  }

  if (freeTrial.isAvailable && disabledSelector) {
    return (
      <Wrapper data-testid="subscriptionmodal--plan-selector">
        <FlexColumn>
          <Spacer height="20px" />
          <FlexRow alignItems="center" gap="8px">
            <PremiumBadgeIcon height="20px" />
            <H5>Upgrade Plan</H5>
            {tagContent}
          </FlexRow>
          <Spacer height="24px" />
          <H2>
            Get started with
            <br />
            Kittl {plan === SubscriptionPlanType.PRO ? 'Pro' : 'Expert'}
          </H2>
          <Spacer height="12px" />
          <P2>
            You won’t be charged until your free trial has ended. Feel free to
            cancel anytime.
          </P2>
          <Spacer height="40px" />
          {freeTrialConfig.content}
          <Spacer height="40px" />
        </FlexColumn>
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="subscriptionmodal--plan-selector">
      {!disabledSelector && !upgradeToExpertPlan && (
        <FlexRow>
          <SelectButton
            isActive={plan === SubscriptionPlanType.PRO}
            onClick={(): void => setPlan(SubscriptionPlanType.PRO)}
          >
            Pro
          </SelectButton>
          <SelectButton
            isActive={plan === SubscriptionPlanType.EXPERT}
            onClick={(): void => setPlan(SubscriptionPlanType.EXPERT)}
          >
            Expert
          </SelectButton>
        </FlexRow>
      )}
      <PlansContainer plan={plan}>
        <PricingPlan
          {...pricingPlansData.PRO}
          centerContent
          index={1}
          quantity={quantity}
          type={SubscriptionPlanType.PRO}
          yearly={yearly}
        />
        <PricingPlan
          {...pricingPlansData.EXPERT}
          centerContent
          index={2}
          quantity={quantity}
          type={SubscriptionPlanType.EXPERT}
          yearly={yearly}
        />
      </PlansContainer>
      <HideMobile>
        <Divider />
        <Spacer height="12px" />
        <FlexRow justifyContent="space-between">
          <a href={HELP_PAGE_URL} rel="noreferrer" target="_blank">
            <IconButton
              height="15px"
              icon={HelpIcon}
              label="Help"
              name="planSelectorHelpButton"
              theme={helpTheme}
              useActiveUnderline={false}
            />
          </a>
          <a
            href={PRICING_PAGE_URL}
            rel="noreferrer"
            style={{ textDecoration: 'none' }}
            target="_blank"
          >
            <CompareLink>Compare Plans</CompareLink>
          </a>
        </FlexRow>
      </HideMobile>
    </Wrapper>
  );
};

export default withEventScope(PlanSelector, { module: 'planSelector' });
